import "./dataset-style.scss";

import {
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddAlt1Outlined from "@mui/icons-material/PersonAddAlt1Outlined";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { usePermission } from "src/helpers/Auth";
import ShareDialog from "src/reusable/components/ShareDialog";
import { CreateItemButton, MenuButton } from "../forms/FormUtils";
import {
  downloadDatasetFile,
  useDatasetList,
  useGetDatasetType,
} from "./services/DatasetServices";

import CIcon from "@coreui/icons-react";
import ChatOutlined from "@mui/icons-material/ChatOutlined";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import FolderIcon from "@mui/icons-material/Folder";
import PropTypes from "prop-types";
import { Can } from "src/helpers/Auth";
import { ListSelect } from "src/reusable";
import { PageLayout } from "src/reusable/components/DashboardComponents";
import { PowerBIDataModal } from "../../reusable/components/PowerBIComponents";
import { CreateDatasetFolderModal } from "../forms/DatasetModals";
import { CreateDatasetModal } from "../forms/datasetModels/CreateDatasetModal";
import DatasetPageView from "./DatasetPageView";
import { PageTopHelpButton } from "src/components/help";
import MovedToLibraryNotice from "src/components/MovedToLibraryNotice";
///home/nuclearn/frontend/src/views/datasets/services/DatasetServices.jsx
import { freeSet } from "@coreui/icons";
import { useDispatch } from "react-redux";
import { nuclearn_api_url } from "src/App";
import { useGetDatasetFolder } from "src/views/datasets/services/DatasetServices.jsx";
import { useChatStore } from "../assistant/stores/ChatStore";
import { setSelectedModelVersion } from "../datasetScoring/DatasetScoringSlice";
import { useModelDatasets } from "../models/ModelService";

const DownloadDatasetButton = ({ datasetId }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { data: datasetType } = useGetDatasetType(datasetId);

  return (
    <MenuButton
      className="download-dataset-button"
      text="Download"
      color="dark"
      size="sm"
      variant="outline"
      data-attr="datasets:btn:download-dataset"
      selected={isDownloading}
      onClick={() => {
        if (!isDownloading) {
          setIsDownloading(true);
          downloadDatasetFile(datasetId, datasetType, () => {
            setIsDownloading(false);
          });
        }
      }}
      icon={
        isDownloading ? (
          <img
            src="/images/NuclearN1-B.gif"
            alt="Loading..."
            style={{
              height: "17px",
            }}
          />
        ) : (
          <CIcon
            content={freeSet.cilDataTransferDown}
            className="text-success"
          />
        )
      }
    />
  );
};

DownloadDatasetButton.propTypes = {
  datasetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

const Datasets = () => {
  const { folder_id } = useParams();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { setDropdownDatasets } = useChatStore();
  // try and use the queryKey datasetFolders to get the folder name by id
  const { data: datasetFolder, isLoading: datasetFolderLoading } =
    useGetDatasetFolder(folder_id);
  const [isDatasetDataModalVisible, setIsDatasetDataModalVisible] =
    useState(false);
  const [datasetDataModalDataset, setDatasetDataModalDataset] = useState({});
  const [openShareFolder, setOpenShareFolder] = useState(false);
  const currentUser = useGetUserDetails();
  const { canIt: isAdmin } = usePermission("admin");
  const folderOwnerId = datasetFolder?.create_user_fk;
  const folderOwnedByMe = !!currentUser && folderOwnerId === currentUser.id;
  const folderOwnerLabel = folderOwnedByMe
    ? "You"
    : datasetFolder?.create_user?.user_display_name ||
      datasetFolder?.create_user?.username;
  const folderIsPublic = datasetFolder?.is_public;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setSelectedModelVersion(0));
  }, []);

  const handleChatWithFolder = () => {
    if (!datasetFolder) return;

    // Get all datasets from the folder
    const folderDatasets = datasetFolder?.datasets || [];

    // Set the datasets in the AtomAssist store
    setDropdownDatasets(
      folderDatasets.map((ds) => ({
        id: ds.id,
        name: ds.name,
        description: ds.description,
      })),
    );

    // Set an initial message to guide the user
    // TODO: This was broken but merging to get the core feature in
    // setInitialUserMessage(
    //   `I'd like to chat about the datasets in folder: ${datasetFolder.name}`
    // );

    // Navigate to AtomAssist with folder info in state
    history.push({
      pathname: "/assistant",
      state: {
        preloadDatasets: folderDatasets.map((ds) => ({
          id: ds.id,
          name: ds.name,
          description: ds.description,
        })),
      },
    });
  };
  return (
    <>
      <PageLayout>
        <MovedToLibraryNotice noticeId="datasets-page" sx={{ mt: 1 }}>
          Finding and browsing datasets is moving to the Library pane in the
          AtomAssist workspace. This page stays available in the meantime.
        </MovedToLibraryNotice>
        <Stack
          data-testid="ds-container"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "stretch" : "flex-end"}
          gap={isMobile ? 2 : 4}
          sx={{ mt: 0.5, mb: 1.5, width: "100%" }}
        >
          <Box>
            {datasetFolderLoading ? (
              <Skeleton variant="text" width={200} height={30} />
            ) : (
              <Box display="flex" alignItems="center" pt={1}>
                {folder_id && (
                  <FolderIcon
                    sx={{ fontSize: "1.5rem", mr: 0.5 }}
                    color="primary"
                  />
                )}
                <Typography
                  variant="h1"
                  fontWeight="bold"
                  fontSize={isMobile ? "1.25rem" : "1.5rem"}
                  data-testid="ds-heading"
                >
                  {folder_id ? `${datasetFolder?.name}` : "Datasets"}
                </Typography>
                {!folder_id && (
                  <Box sx={{ ml: 0.5 }}>
                    <PageTopHelpButton
                      productLabel="Datasets"
                      product="datasets"
                      compact
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Folder detail: owner + visibility (the folder's own access;
                grants here are also applied to datasets added to it). The
                top-level Datasets page needs no subtitle (help lives in the
                global header pill). */}
            {folder_id && !datasetFolderLoading && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                flexWrap="wrap"
                sx={{ mt: 0.5 }}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="textSecondary">
                    {folderOwnerLabel || "—"}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  label={folderIsPublic ? "Public" : "Private"}
                  color={folderIsPublic ? "success" : "default"}
                />
              </Stack>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: isMobile ? 1 : 2,
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
              justifyContent: { xs: "center", md: "flex-end" },
              width: isMobile ? "100%" : "auto",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {folder_id &&
              (folderOwnedByMe ? (
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<PersonAddAlt1Outlined />}
                  onClick={() => setOpenShareFolder(true)}
                  data-testid={`ds-folder-manage-access-${folder_id}`}
                >
                  Manage access
                </Button>
              ) : (
                <Can
                  perform="dataset:update"
                  yes={() => (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      startIcon={<PersonAddAlt1Outlined />}
                      onClick={() => setOpenShareFolder(true)}
                      data-testid={`ds-folder-manage-access-${folder_id}`}
                    >
                      Manage access
                    </Button>
                  )}
                />
              ))}
            {folder_id &&
              datasetFolder?.datasets &&
              datasetFolder.datasets.length > 0 && (
                <Can
                  perform="dataset_data:read"
                  yes={() => (
                    <Tooltip title="Open AtomAssist with all datasets in this folder">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        startIcon={<ChatOutlined />}
                        onClick={handleChatWithFolder}
                        data-testid={`ds-chat-folder-btn-${folder_id}`}
                        data-attr={`datasets:btn:chat-with-folder-${folder_id}`}
                      >
                        Chat with Folder
                      </Button>
                    </Tooltip>
                  )}
                  no={() => (
                    <Tooltip title="You do not have permission to access these datasets">
                      <span>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="medium"
                          startIcon={<ChatOutlined />}
                          disabled
                        >
                          Chat with Folder
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                />
              )}
            <div hidden={folder_id}>
              <Can
                perform="dataset:write"
                yes={() => (
                  <CreateItemButton
                    itemName="Create Dataset Folder"
                    createItemModal={CreateDatasetFolderModal}
                    icon={<CreateNewFolderIcon />}
                    noWrap
                    data-testid="ds-btn-create-folder"
                    data-attr="datasets:btn:create-dataset-folder"
                  />
                )}
                no={() => (
                  <CreateItemButton
                    itemName="Create Dataset Folder"
                    tooltipTitle="User doesn't have permissions dataset:write"
                    noWrap
                    disabled
                    icon={<CreateNewFolderIcon />}
                  />
                )}
              />
            </div>
            <Can
              perform="dataset:write"
              yes={() => (
                <CreateItemButton
                  itemName="Create Dataset"
                  createItemModal={CreateDatasetModal}
                  noWrap
                  data-testid="ds-btn-create-dataset"
                  data-attr="datasets:btn:create-dataset"
                />
              )}
              no={() => (
                <CreateItemButton
                  itemName="Create Dataset"
                  tooltipTitle="User doesn't have permissions dataset:write"
                  noWrap
                  disabled
                />
              )}
            />
          </Box>
        </Stack>
        <Box sx={{ pb: 2 }}>
          <DatasetPageView />
        </Box>
      </PageLayout>
      <PowerBIDataModal
        isVisible={isDatasetDataModalVisible}
        message={` to dataset ${datasetDataModalDataset.name}`}
        url={`${nuclearn_api_url}/datasets/${datasetDataModalDataset.id}/data`}
        forPage="datasets"
        onClose={() => {
          setIsDatasetDataModalVisible(false);
          setDatasetDataModalDataset({});
        }}
      />
      {folder_id && openShareFolder && datasetFolder && (
        <ShareDialog
          open={openShareFolder}
          resource={{
            type: "datasetFolder",
            id: Number(folder_id),
            name: datasetFolder?.name,
          }}
          canTransferOwnership={folderOwnedByMe || isAdmin}
          onClose={() => setOpenShareFolder(false)}
        />
      )}
    </>
  );
};

Datasets.propTypes = {
  location: PropTypes.object,
};

const datasetsNotInModelDatasets = (datasets, modelDatasets) => {
  if (modelDatasets) {
    const modelDatasetIds = modelDatasets.map((md) => {
      return md.id;
    });
    return datasets?.filter((dataset) => !modelDatasetIds.includes(dataset.id));
  } else {
    return datasets;
  }
};

export const DatasetListSelect = (props) => {
  const { data, isLoading } = useDatasetList();
  const { data: modelDatasets, isLoading: modelDatasetsisLoading } =
    useModelDatasets(props.modelId);
  const [selectedDataset, setSelectedDataset] = useState("");

  const datasetDescription = data?.filter(
    (dataset) => dataset.id === selectedDataset,
  )[0]?.description;

  return (
    !isLoading &&
    !modelDatasetsisLoading && (
      <div id="dataset-list-select-wrapper">
        <ListSelect
          disabled={props.isDisabled}
          handleSelectedValue={(x) => {
            setSelectedDataset(x);
            props.handleSelectedDataset(x);
          }}
          selectedValue={props.selectedValue}
          items={datasetsNotInModelDatasets(data, modelDatasets)}
          selectDomID="dataset-list-select"
        />
        {datasetDescription}
      </div>
    )
  );
};

DatasetListSelect.propTypes = {
  handleSelectedDataset: PropTypes.func.isRequired,
  modelId: PropTypes.number,
  selectedValue: PropTypes.any,
  isDisabled: PropTypes.bool,
};

DatasetListSelect.default = {
  modelId: null,
  isDisabled: true,
};

export default Datasets;

export const DataField = (props) => {
  // stylized DataField with a description popover and links and stuff
  return <h4>{props.name}</h4>;
};

DataField.propTypes = {
  name: PropTypes.func.isRequired,
};
